<template>
  <div class="flex flex-col min-h-screen max-h-full h-full bg-blue-dark">
    <!-- <app-footer /> -->
    <div
      v-if="$page.props.auth.assumed"
      class="text-center text-xs bg-navy-dark text-white font-bold py-1"
    >
      <inertia-link class="block" :href="route('login-as.assume-cancel')">
        {{ assumeLink }}
      </inertia-link>
    </div>

    <div class="flex flex-row flex-grow main">
      <div class="relative flex flex-row">
        <left-menu />
        <search :farm="$root.farm" />
      </div>
      <div
        v-show="!$root.lpisLinking"
        class="absolute px-auto text-center md:hidden cursor-pointer z-50 text-2xl rounded-full bg-green bottom-5 right-4 p-4 shadow-xl"
        v-on:click="$root.showMapOnMobile = !$root.showMapOnMobile"
      >
        <svg-icon
          type="mdi"
          :path="mdiMapOutline"
          class="text-white"
          size="28"
          v-if="!$root.showMapOnMobile"
        />

        <svg-icon
          type="mdi"
          :path="mdiPageLayoutSidebarLeft"
          class="text-white"
          size="28"
          v-else
        />
      </div>
      <transition name="slide-width-fade">
        <main
          ref="mainblock"
          class="bg-gray-100 relative z-30 w-full"
          :style="panelStyle"
          :class="{ hidden: $root.showMapOnMobile, 'md:block': true }"
        >
          <div class="flex h-full">
            <!-- Trigger Main Map on mobile-->

            <div class="flex flex-col h-full w-full overflow-y-auto p-4 md:p-5" style="scrollbar-gutter: stable;">
              <slot />

              <div class="py-2"></div>
            </div>

            <div class="absolute -right-[18px] top-1/2 hidden md:block">
              <button
                type="button"
                class="block w-9 h-9 rounded-full cursor-col-resize focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy"
                :title="handleLabel + ' (drag to resize)'"
                :aria-label="handleLabel"
                :aria-expanded="isExpanded.toString()"
                @mousedown="drag"
                @keydown.enter.prevent="togglePanel"
                @keydown.space.prevent="togglePanel"
              >
                <img
                  src="/img/slider.png"
                  alt=""
                  class="w-9 h-9 pointer-events-none transition-transform duration-200"
                  :class="{ 'flip-horizontal': isExpanded }"
                  draggable="false"
                />
              </button>
            </div>
          </div>
        </main>
      </transition>

      <farm-map
        :farmer="$root.farmer"
        :LpisHerdNo="$root.Lpis.herdNo"
        :showLpis="$root.Lpis.show"
        :showFarmManager="$root.showFarmManager"
        :paths="$root.paths.samples"
        :fields="$root.fields"
        :sampleZones="$root.sampleZones"
        :apikey="$page.props.config.arcgis_key"
        :center="$page.props.config.arcgis_map_center_lat_lon"
        :zoom="$page.props.config.arcgis_map_center_zoom"
        :sampler_location="$root.services.sampler_location"
        :dropoff_points="$root.services.dropoff_points"
        :lpisUrl="$page.props.config.lpis_server_url"
        :lpisSource="$root.Lpis.source"
        :dafmLpis="$page.props.config.dafm_lpis"
        :lpisLinkedIds="$root.farmer?.lpis_linked_ids"
        :show-layer-toggle="true"
        :soilAnalysisContext="$root.soilAnalysisContext"
      />
      <account-menu />
    </div>
  </div>
</template>

<script>
import AppFooter from "@/Partials/Footer";
import LeftMenu from "@/Partials/LeftMenu";
import FarmMap from "@/Partials/FarmMap";
import Search from "@/Partials/Search";
import AccountMenu from "@/Partials/Menu/AccountMenu";
import { mdiMapOutline, mdiPageLayoutSidebarLeft } from "@mdi/js";

const PANEL_RATIO_KEY = "livefarm.panel-width-ratio";
const MOBILE_BREAKPOINT = 768;
const CLICK_MAX_MOVE_PX = 4;
const CLICK_MAX_DURATION_MS = 300;

export default {
  components: {
    AppFooter,
    LeftMenu,
    FarmMap,
    Search,
    AccountMenu,
  },
  data() {
    return {
      mdiMapOutline,
      mdiPageLayoutSidebarLeft,
      showMenu: false,
      panelWidth: null, // runtime width in px (desktop only)
      minPanelWidthPx: 660, // absolute floor for the content panel
      minPanelRatio: 0.45, // ...but never more than this share of the parent
      minMapWidth: 280, // the map always keeps at least this much
      initialRatio: 0.58, // starting share of the parent
      restoreWidth: null, // width to return to when un-expanding
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.ensureInitialWidth();
      window.addEventListener("resize", this.handleResize);
    });
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
  },
  created() {
    this.$root.showMenu = this.showMenu;
  },
  watch: {
    showMenu(newValue) {
      this.$root.showMenu = newValue;
    },
  },
  computed: {
    assumeLink() {
      const user = this.$page.props.auth.user;
      return `You are logged in as ${user.first_name} ${user.last_name}. Click to return to your account`;
    },
    panelStyle() {
      if (!this.panelWidth || this.isMobile()) return {};
      return { width: this.panelWidth + "px" };
    },
    isExpanded() {
      return this.restoreWidth !== null;
    },
    handleLabel() {
      return this.isExpanded ? "Restore panel width" : "Expand panel";
    },
  },
  methods: {
    isMobile() {
      return typeof window === "undefined" || window.innerWidth < MOBILE_BREAKPOINT;
    },
    parentWidth() {
      const block = this.$refs.mainblock;
      if (!block) return null;
      return block.parentElement?.clientWidth || window.innerWidth;
    },
    // The panel minimum scales down on small screens so it can never exceed the
    // space available; the map keeps minMapWidth at the other end.
    panelBounds(parentWidth) {
      const min = Math.min(
        this.minPanelWidthPx,
        Math.floor(parentWidth * this.minPanelRatio)
      );
      const max = Math.max(min, parentWidth - this.minMapWidth);
      return { min, max };
    },
    clampPanelWidth(width, parentWidth) {
      const { min, max } = this.panelBounds(parentWidth);
      return Math.min(Math.max(width, min), max);
    },
    // Stored as a ratio, not pixels: a width chosen on a 32" monitor would
    // otherwise be restored verbatim on a laptop and overflow it.
    readStoredRatio() {
      try {
        const raw = window.localStorage.getItem(PANEL_RATIO_KEY);
        const ratio = raw === null ? NaN : parseFloat(raw);
        return Number.isFinite(ratio) && ratio > 0 && ratio <= 1 ? ratio : null;
      } catch (e) {
        return null; // storage blocked (private browsing, and similar)
      }
    },
    storeRatio(width, parentWidth) {
      if (!width || !parentWidth) return;
      try {
        window.localStorage.setItem(
          PANEL_RATIO_KEY,
          (width / parentWidth).toFixed(4)
        );
      } catch (e) {
        // storage unavailable - the width simply is not remembered
      }
    },
    handleResize() {
      if (this.isMobile()) {
        this.panelWidth = null; // mobile is full width
        return;
      }
      const parentWidth = this.parentWidth();
      if (!parentWidth) return;
      if (!this.panelWidth) {
        this.ensureInitialWidth(); // returning to desktop from mobile
        return;
      }
      this.panelWidth = this.clampPanelWidth(this.panelWidth, parentWidth);
    },
    ensureInitialWidth() {
      if (typeof window === "undefined") return;
      if (this.isMobile()) {
        this.panelWidth = null;
        return;
      }
      const parentWidth = this.parentWidth();
      if (!parentWidth) return;
      const ratio = this.readStoredRatio() ?? this.initialRatio;
      const target = this.panelWidth ?? Math.floor(parentWidth * ratio);
      this.panelWidth = this.clampPanelWidth(target, parentWidth);
    },
    togglePanel() {
      if (this.isMobile()) return;
      const parentWidth = this.parentWidth();
      if (!parentWidth) return;
      const { max } = this.panelBounds(parentWidth);
      if (this.isExpanded) {
        this.panelWidth = this.clampPanelWidth(this.restoreWidth, parentWidth);
        this.restoreWidth = null;
      } else if (this.panelWidth >= max) {
        // Already as wide as it goes, so the handle collapses back to the
        // default share rather than doing nothing.
        this.panelWidth = this.clampPanelWidth(
          Math.floor(parentWidth * this.initialRatio),
          parentWidth
        );
      } else {
        this.restoreWidth = this.panelWidth;
        // Expand to the maximum, never to full width: a zero-size container
        // breaks the ArcGIS MapView.
        this.panelWidth = max;
      }
      this.storeRatio(this.panelWidth, parentWidth);
    },
    drag(e) {
      if (this.isMobile()) return; // disable drag on mobile
      const parentWidth = this.parentWidth();
      if (!parentWidth) return;
      const { min, max } = this.panelBounds(parentWidth);
      const block = this.$refs.mainblock;
      if (!this.panelWidth) {
        this.panelWidth = Math.max(block.offsetWidth, min);
      }

      const startX = e.clientX;
      const startedAt = Date.now();
      let dragX = startX;
      let moved = false;
      const vm = this;

      document.onmousemove = function (ev) {
        let proposed = vm.panelWidth + ev.clientX - dragX;
        if (proposed < min) proposed = min;
        if (proposed > max) proposed = max;
        if (proposed !== vm.panelWidth) {
          moved = true;
          vm.panelWidth = proposed;
          dragX = ev.clientX;
        }
      };

      document.onmouseup = function (ev) {
        document.onmousemove = document.onmouseup = null;
        // A press that neither moved the pointer nor lasted long is a click on
        // the handle, which reads as a button, so treat it as one.
        const isClick =
          !moved &&
          Math.abs(ev.clientX - startX) < CLICK_MAX_MOVE_PX &&
          Date.now() - startedAt < CLICK_MAX_DURATION_MS;
        if (isClick) {
          vm.togglePanel();
          return;
        }
        vm.restoreWidth = null; // a manual drag supersedes the expanded state
        vm.storeRatio(vm.panelWidth, parentWidth);
      };
    },
  }
};
</script>
